import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '../../components/core';
import type { ResultRow } from './types';

interface Props {
  results: ResultRow[];
  cropTypeName: (_id: string) => string;
  regionName: (_id: string) => string;
  groupByCropType: boolean;
  collapsedGroups: Set<string>;
  onToggleGroup: (_cropTypeId: string) => void;
  onViewCustomer: (_customerId: string) => void;
  isUpcoming: boolean;
}

export function ResultsTable({
  results,
  cropTypeName,
  regionName,
  groupByCropType,
  collapsedGroups,
  onToggleGroup,
  onViewCustomer,
  isUpcoming,
}: Props) {
  const { t } = useTranslation();

  // Group rows by cropTypeId (order of first appearance, already sorted by lastName)
  const groups: Array<{ cropTypeId: string; rows: ResultRow[] }> = [];
  if (groupByCropType) {
    const map = new Map<string, ResultRow[]>();
    for (const row of results) {
      const existing = map.get(row.cropTypeId);
      if (existing) existing.push(row);
      else {
        map.set(row.cropTypeId, [row]);
        groups.push({ cropTypeId: row.cropTypeId, rows: map.get(row.cropTypeId)! });
      }
    }
  }

  const borderColor  = isUpcoming ? 'border-amber-200' : 'border-green-200';
  const rowSeparator = isUpcoming ? 'border-amber-100' : 'border-green-100';
  const rowHover     = isUpcoming ? 'hover:bg-amber-50' : 'hover:bg-green-50';

  return (
    <div className={`hidden overflow-hidden rounded-xl border bg-white shadow-sm sm:block ${borderColor}`}>
      <table className="w-full">
        <thead>
          <tr className={`border-b bg-gray-50 ${borderColor}`}>
            {[
              t('cropStatus.colFirstName'),
              t('cropStatus.colLastName'),
              t('cropStatus.colPhone'),
              t('cropStatus.colAddress'),
              ...(groupByCropType ? [] : [t('cropStatus.colCropType')]),
              t('cropStatus.colRegion'),
            ].map(col => (
              <th key={col} className="px-5 py-2.5 text-start font-body text-xs font-bold uppercase tracking-wider text-gray-500">
                {col}
              </th>
            ))}
            <th className="px-5 py-2.5 text-end font-body text-xs font-bold uppercase tracking-wider text-gray-500">
              {t('cropStatus.colAction')}
            </th>
          </tr>
        </thead>
        <tbody>
          {groupByCropType
            ? groups.map(({ cropTypeId, rows }) => {
                const isCollapsed = collapsedGroups.has(cropTypeId);
                const count = rows.length;
                return (
                  <React.Fragment key={cropTypeId}>
                    {/* Group header row */}
                    <tr className={`border-t-2 first:border-t-0 ${rowSeparator}`}>
                      <td colSpan={6} className="bg-earth-50 px-4 py-2">
                        <button
                          type="button"
                          onClick={() => onToggleGroup(cropTypeId)}
                          aria-expanded={!isCollapsed}
                          aria-label={isCollapsed
                            ? t('cropStatus.expandGroup', { name: cropTypeName(cropTypeId) })
                            : t('cropStatus.collapseGroup', { name: cropTypeName(cropTypeId) })}
                          className="flex w-full items-center gap-2 text-start"
                        >
                          <span className={`block h-4 w-4 flex-shrink-0 text-earth transition-transform duration-200 ${isCollapsed ? 'rtl:rotate-180' : 'rotate-90'}`}>
                            <ChevronRightIcon />
                          </span>
                          <span className="inline-flex items-center rounded-full bg-earth-light px-2.5 py-0.5 font-body text-xs font-bold text-earth">
                            {cropTypeName(cropTypeId)}
                          </span>
                          <span className="font-body text-xs text-gray-500">
                            {t('cropStatus.groupCount', { count })}
                          </span>
                        </button>
                      </td>
                    </tr>
                    {/* Data rows */}
                    {!isCollapsed && rows.map((row, i) => (
                      <tr
                        key={`${row.customer.id}|${row.cropTypeId}|${row.regionId}`}
                        className={`${rowHover} ${i < rows.length - 1 ? `border-b ${rowSeparator}` : ''}`}
                      >
                        <td className="py-3 ps-8 pe-5 font-body font-semibold text-gray-900">{row.customer.firstName}</td>
                        <td className="px-5 py-3 font-body text-gray-900">{row.customer.lastName}</td>
                        <td className="w-36 px-5 py-3 font-body text-sm text-gray-700"><a href={`tel:${row.customer.phone}`} className="text-green-700 hover:underline">{row.customer.phone}</a></td>
                        <td className="max-w-[200px] truncate px-5 py-3 font-body text-sm text-gray-500">{row.customer.address}</td>
                        <td className="w-28 px-5 py-3">
                          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-green-100 px-2.5 py-0.5 font-body text-xs font-semibold text-green-800">
                            {regionName(row.regionId)}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-end">
                          <button
                            type="button"
                            onClick={() => onViewCustomer(row.customer.id)}
                            className="font-body text-sm font-medium text-green-700 hover:text-green-900"
                          >
                            {t('cropStatus.viewCustomer')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            : results.map((row, i) => (
                <tr
                  key={`${row.customer.id}|${row.cropTypeId}|${row.regionId}`}
                  className={`${rowHover} ${i < results.length - 1 ? `border-b ${rowSeparator}` : ''}`}
                >
                  <td className="px-5 py-3 font-body font-semibold text-gray-900">{row.customer.firstName}</td>
                  <td className="px-5 py-3 font-body text-gray-900">{row.customer.lastName}</td>
                  <td className="w-36 px-5 py-3 font-body text-sm text-gray-700"><a href={`tel:${row.customer.phone}`} className="text-green-700 hover:underline">{row.customer.phone}</a></td>
                  <td className="max-w-[200px] truncate px-5 py-3 font-body text-sm text-gray-500">{row.customer.address}</td>
                  <td className="w-28 px-5 py-3">
                    <span className="inline-flex items-center whitespace-nowrap rounded-full bg-earth-light px-2.5 py-0.5 font-body text-xs font-semibold text-earth">
                      {cropTypeName(row.cropTypeId)}
                    </span>
                  </td>
                  <td className="w-28 px-5 py-3">
                    <span className="inline-flex items-center whitespace-nowrap rounded-full bg-green-100 px-2.5 py-0.5 font-body text-xs font-semibold text-green-800">
                      {regionName(row.regionId)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-end">
                    <button
                      type="button"
                      onClick={() => onViewCustomer(row.customer.id)}
                      className="font-body text-sm font-medium text-green-700 hover:text-green-900"
                    >
                      {t('cropStatus.viewCustomer')}
                    </button>
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
